import Data from '@easy-expense/frontend-data-layer';
import { getTranslation, useIntlStore } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import { dateFormatSkeleton } from '@easy-expense/utils-shared';
import {
  useMaterialReactTable,
  MaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import React from 'react';
import { Image } from 'react-native';

import { ReconciledStatementRow } from './reconcile';
import { CategoryBadge } from '../Expense/CategoryBadge.component';
import { ReceiptButton } from '../Expense/ReceiptButton.component';
import { ReceiptSideModal } from '../Expense/ReceiptSideModal.component';
import { Circle } from '../Shape.components';
import { TableTextSize } from '../Shared/TableTextSize';

function getDateTimestamp(dateStr: string | number | undefined): number {
  if (!dateStr) {
    return 0;
  }
  const date = new Date(String(dateStr));
  if (isNaN(date.getTime())) {
    return 0;
  }
  return date.getTime();
}

export const ReconciliationTables: React.FC<{
  reconciledStatement: ReconciledStatementRow[];
  dateHeader: string;
  totalHeader: string;
  statementDescriptorHeader?: string;
}> = ({ reconciledStatement, dateHeader, totalHeader, statementDescriptorHeader }) => {
  const { formatCurrency, formatDate } = useIntlStore();
  const [showReceiptModal, setShowReceiptModal] = React.useState(false);
  const [modalReceipts, setModalReceipts] = React.useState<string[]>([]);

  function onReceiptButtonClick(uri?: string) {
    if (!uri || typeof uri !== 'string' || uri.trim() === '') {
      return;
    }
    setModalReceipts([String(uri)]);
    setShowReceiptModal(true);
  }

  // Column definitions for reconciled statement
  const reconciledColumns: MRT_ColumnDef<ReconciledStatementRow>[] = React.useMemo(
    () => [
      {
        accessorKey: 'Status',
        header: getTranslation('Status'),
        size: 150,
        Cell: ({ row }) => {
          const status = row.original.Status ?? '';
          const isReconciled = status === 'Reconciled';
          return (
            <Layout.Row
              align
              px={8}
              py={4}
              radius={4}
              style={{
                backgroundColor: isReconciled
                  ? theme.colors.successXLight
                  : theme.colors.destructiveXLight,
                display: 'inline-flex',
              }}
            >
              {isReconciled ? (
                <Icon name="checkmark-circle" size={20} color={theme.colors.success} />
              ) : (
                <Icon name="close-circle" size={20} color={theme.colors.destructive} />
              )}
              <Spacer.Horizontal size={8} />
              <OpenSans.Primary size={TableTextSize.s14}>{status}</OpenSans.Primary>
            </Layout.Row>
          );
        },
      },
      ...(statementDescriptorHeader
        ? [
            {
              accessorKey: statementDescriptorHeader,
              header: getTranslation('Statement'),
              size: 200,
              Cell: ({ row }) => {
                const descriptor = row.original[statementDescriptorHeader] ?? '';
                return (
                  <OpenSans.Secondary size={TableTextSize.s14} weight="bold-700">
                    {String(descriptor)}
                  </OpenSans.Secondary>
                );
              },
            } as MRT_ColumnDef<ReconciledStatementRow>,
          ]
        : []),
      {
        accessorKey: dateHeader,
        header: getTranslation('Date'),
        size: 120,
        sortingFn: (rowA, rowB) => {
          const dateA = getDateTimestamp(
            rowA.original[dateHeader] ?? rowA.original['Receipt Date*'] ?? '',
          );
          const dateB = getDateTimestamp(
            rowB.original[dateHeader] ?? rowB.original['Receipt Date*'] ?? '',
          );
          return dateA - dateB;
        },
        Cell: ({ row }) => {
          const dateValue = row.original[dateHeader] ?? row.original['Receipt Date*'] ?? '';
          if (!dateValue) {
            return null;
          }
          const date = new Date(String(dateValue));
          if (isNaN(date.getTime())) {
            return (
              <OpenSans.Primary
                size={TableTextSize.s14}
                weight="bold-700"
                style={{ color: theme.colors.black }}
              >
                {String(dateValue)}
              </OpenSans.Primary>
            );
          }
          return (
            <OpenSans.Primary
              size={TableTextSize.s14}
              weight="bold-700"
              style={{ color: theme.colors.black }}
            >
              {formatDate(date, {
                skeleton: dateFormatSkeleton.abbreviated,
              })}
            </OpenSans.Primary>
          );
        },
      },
      {
        accessorKey: totalHeader,
        header: getTranslation('Total'),
        size: 120,
        Cell: ({ row }) => {
          const total =
            typeof row.original[totalHeader] === 'string'
              ? parseFloat(row.original[totalHeader])
              : (row.original[totalHeader] ?? 0);
          return (
            <OpenSans.Primary size={TableTextSize.s14} weight="bold-700">
              {formatCurrency(total)}
            </OpenSans.Primary>
          );
        },
      },
      {
        accessorKey: 'Vendor*',
        header: getTranslation('Matched Vendor'),
        size: 200,
        Cell: ({ row }) => {
          const vendorName = row.original['Vendor*'] ?? row.original.Vendor ?? '';
          if (!vendorName) {
            return null;
          }

          // Look up vendor by name to get the image
          const vendors = Data.vendors.get();
          const vendor = vendors.find((v) => v.value?.name === vendorName);
          const vendorValue = vendor?.value;

          return (
            <Layout.Row style={{ maxWidth: 250 }} align>
              {vendorValue?.image ? (
                <Image
                  source={{ uri: vendorValue.image }}
                  style={{
                    height: 24,
                    width: 24,
                    borderRadius: 100,
                    borderWidth: 1,
                    borderColor: theme.colors.grayLight,
                  }}
                />
              ) : (
                <Circle.Solid bg="brandPrimaryLight" circleSize={24} center>
                  <OpenSans.Primary size={TableTextSize.xs}>{vendorName[0]}</OpenSans.Primary>
                </Circle.Solid>
              )}
              <Spacer.Horizontal size={8} />
              <Layout.Row grow style={{ overflow: 'hidden' }}>
                <OpenSans.Primary size={TableTextSize.s14} style={{ color: theme.colors.black }}>
                  {vendorName}
                </OpenSans.Primary>
              </Layout.Row>
            </Layout.Row>
          );
        },
      },
      {
        accessorKey: 'Category*',
        header: getTranslation('Category'),
        size: 200,
        Cell: ({ row }) => {
          const categoryName = row.original['Category*'] ?? row.original.Category ?? '';
          if (!categoryName) {
            return null;
          }

          // Look up category by name to get the category value
          const categories = Data.expenseCategories.get();
          const category = categories.find((c) => c.value?.name === categoryName);
          const categoryValue = category?.value;

          return <CategoryBadge category={categoryValue} />;
        },
      },
      {
        accessorKey: 'Attachments*',
        header: getTranslation('Receipt'),
        size: 100,
        Cell: ({ row }) => {
          const receiptURL = row.original['Attachments*'] ?? row.original.Attachments;
          if (!receiptURL || typeof receiptURL !== 'string' || receiptURL.trim() === '') {
            return null;
          }
          return (
            <Layout.Row align justify="center">
              <ReceiptButton
                receipt={String(receiptURL)}
                setModalOpen={() => onReceiptButtonClick(String(receiptURL))}
              />
            </Layout.Row>
          );
        },
      },
    ],
    [dateHeader, totalHeader, statementDescriptorHeader, formatCurrency],
  );

  // Table for reconciled statement
  const reconciledTable = useMaterialReactTable({
    columns: reconciledColumns,
    data: reconciledStatement,
    initialState: {
      showGlobalFilter: true,
      density: 'compact',
      sorting: [{ desc: true, id: dateHeader }],
      pagination: { pageIndex: 0, pageSize: 25 },
    },
    icons: {
      CloseIcon: () => (
        <Icon
          name="close"
          size={18}
          color={theme.colors.secondary}
          style={{ padding: 0, margin: 0 }}
        />
      ),
      SearchIcon: () => (
        <Icon name="search" size={18} color={theme.colors.secondary} style={{ marginRight: 8 }} />
      ),
    },
    enableColumnActions: false,
    enableDensityToggle: false,
    getRowId: (_row, index) => `reconciled-${index}`,
    muiTableHeadCellProps: {
      sx: {
        '& .Mui-TableHeadCell-Content': {
          color: theme.colors.secondary,
          fontWeight: 'regular',
          fontSize: `${TableTextSize.s14}px`,
        },
      },
    },
    muiTableBodyRowProps: {
      sx: {
        cursor: 'default',
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: '0',
        border: '',
      },
    },
  });

  return (
    <>
      <Layout.Column>
        <MaterialReactTable table={reconciledTable} />
      </Layout.Column>
      <ReceiptSideModal
        modalOpen={showReceiptModal}
        setModalState={setShowReceiptModal}
        receipts={modalReceipts}
      />
    </>
  );
};
